import LoadingWrapper from 'shared/components/loading-wrapper';
import moment from 'moment';
import t from 'react-translate';
import { RootState } from 'redux/schemas';
import { connect } from 'react-redux';
import { css } from '@emotion/core';
import { getLicenseData } from 'redux/actions/license';
import { gray5, gray7, primary } from 'styles/global_defaults/colors';
import { halfSpacing, largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { handheld, notHandheld } from 'styles/global_defaults/media-queries';
import { useEffect } from 'react';
import RadialGauge, { Mode } from './radial-gauge';

type StateProps = {
  licensesTotal: number,
  licensesUsed: number,
  // TODO: This should probably be the Current Institution object, selected out of the store
  currentInstitutionId: number,
  licenseNotes: string,
  loaded: boolean, // Whether data has finished loading, controls the loading spinner
};

type DispatchProps = {
  getLicenseData,
};

type OwnProps = {
  startDate: moment.Moment,
  endDate: moment.Moment,
};

const LicenseUsagePanel = (props: StateProps & DispatchProps & OwnProps) => {
  useEffect(() => {
    if (props.currentInstitutionId !== null) {
      props.getLicenseData({ institutionId: props.currentInstitutionId });
    }
  }, [props.currentInstitutionId]);

  const [startDateString, endDateString] = [props.startDate, props.endDate].map(d => d.format('MM/DD/YYYY'));
  const style = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: ${gray7};

    & > *:last-child {
      margin-top: ${largeSpacing}px;
    }

    h1 {
      margin-bottom: ${halfSpacing}px;
    }

    .contract-period {
      margin-top: ${halfSpacing}px;
      margin-bottom: ${largeSpacing}px;
    }

    .license-notes {
      max-width: 600px;

      /* Use double margin on mobile to account for the radial labels repositioning beneath the gauge */
      ${handheld(css`
        margin-top: ${largeSpacing * 2}px;
      `)};
      ${notHandheld(css`
        margin-top: ${largeSpacing}px;
      `)};
    }

    .gauge-container {
      /* mx-auto is not being obeyed in IE11 for this element */
      align-self: center;
    }

    /* Fixes an Edge specific bug where a colored border is rendered beneath the gauge due to Edge incorrectly masking the colored gauges */
    .meter-container {
      background-color: ${gray7};
      border-color: ${gray7};
      border-width: 1px;
      border-bottom-style: solid;
    }
  `;

  return (
    <div css={style} className='mx-auto'>
      <div className='text-xl text-center font-weight-light'>{t.INSTITUTIONS.LICENSES.DASHBOARD.LICENSE_USAGE()}</div>
      <LoadingWrapper isLoaded={props.loaded}>
        {/* TODO: For some inexplicable reason this line causes a build error, only when starting storybook. Needs to be commented out before running storybook and can be re-enabled after */}
        <div className='contract-period text-center text-medium font-weight-bolder condensed'>{t.INSTITUTIONS.LICENSES.DASHBOARD.FOR_CURRENT_CONTRACT_PERIOD(endDateString, startDateString)}</div>
        <div className='mx-auto gauge-container'>
          <RadialGauge
            mode={Mode.HALF_CIRCLE}
            activeColor={primary}
            currentLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.LICENSES_USED(props.licensesUsed)}
            min={0}
            max={props.licensesTotal}
            current={props.licensesUsed}
            bkgColor={gray7}
            hideMeter={!props.licensesTotal}
          />
        </div>
        <div className='mx-auto text-center license-notes'>
          {props.licenseNotes}
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state, ownProps) => ({
    ...ownProps,
    licensesTotal: state.models.license.noSeats,
    licensesUsed: state.models.license.seatUsage,
    loaded: state.models.license.loaded,
    currentInstitutionId: state.app.currentInstitutionId,
    licenseNotes: state.models.license.notes,
  }),
  {
    getLicenseData,
  },
)(LicenseUsagePanel);
